import { useCallback, useEffect, useRef } from "react";
import { TempoElement } from "@/utils/tempoElement";
import { NavTreeNode } from "@/utils/types";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useExtractElementStore } from "@/components/Canvas/stores/extractElementStore";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { createInstantUpdateWithShell } from "@/components/Canvas/IframePool/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import {
  SimpleCanvasRef,
  TEMP_ELEMENT_DRAG_STORYBOARD_ID,
} from "@/components/SimpleCanvas/SimpleCanvas";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useActiveCanvas, useAutoUpdateRef } from "@/utils/utils";
import { useGetCopyDataForSelectedElements } from "@/components/Canvas/hooks/useCopyPaste";
import { OutlineSystemRef } from "../../OutlineSystem";
import _ from "lodash";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { useDispatch } from "react-redux";
import { setHoveredElementKey } from "@/store/iframeSlice";

interface ElementDragState {
  isDragging: boolean;
  isDraggingAbsolute: boolean;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  temporaryStoryboardId: string | null;
  originalSelectedElement: TempoElement | null;
  currentStoryboardId: string | null; // Track which storyboard we're dragging in
  draggingElement: HTMLElement | null; // Store reference to the dragging element
  draggingNavTreeNode: NavTreeNode | null;
  originalDisplay: string | null;
}

interface UseDragElementProps {
  storyboardId: string;
  simpleCanvasRef?: React.RefObject<SimpleCanvasRef>;
  iframeDocRef: React.MutableRefObject<Document | null>;
  outlineSystemRef: React.RefObject<OutlineSystemRef>;
}

interface UseDragElementReturn {
  isDragging: boolean;
  isDraggingAbsolute: boolean;
  onMouseDown: (
    event: MouseEvent,
    selectedNavNode: NavTreeNode | null,
    domElement: HTMLElement | null,
  ) => void;
  onMouseMove: (
    event: MouseEvent,
    selectedNavNode: NavTreeNode | null,
    isDrawing: boolean,
  ) => Promise<boolean>; // Returns true if handled
  onMouseUp: () => void;
}

export const useDragElement = ({
  storyboardId,
  simpleCanvasRef,
  iframeDocRef,
  outlineSystemRef,
}: UseDragElementProps): UseDragElementReturn => {
  const activeCanvas = useActiveCanvas();
  const storyboardIdRef = useAutoUpdateRef(storyboardId);

  const storyboard = activeCanvas?.storyboards.find(
    (s) => s.id === storyboardId,
  );
  const storyboardRef = useAutoUpdateRef(storyboard);

  const getCopyDataForSelectedElements = useGetCopyDataForSelectedElements();
  const getCopyDataForSelectedElementsRef = useAutoUpdateRef(
    getCopyDataForSelectedElements,
  );
  const initializeStoryboard = useInitializeStoryboard();
  const initializeStoryboardRef = useAutoUpdateRef(initializeStoryboard);

  const dispatch = useDispatch();

  // Element drag state
  const elementDragStateRef = useRef<ElementDragState>({
    isDragging: false,
    isDraggingAbsolute: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    temporaryStoryboardId: null,
    originalSelectedElement: null,
    currentStoryboardId: null,
    draggingElement: null,
    draggingNavTreeNode: null,
    originalDisplay: null,
  });

  const startStoryboardDrag = useCallback(
    async (
      event: {
        offsetX: number;
        offsetY: number;
      },
      selectedNavNode: NavTreeNode,
      position: string,
    ) => {
      const copyData = await getCopyDataForSelectedElementsRef.current();
      if (!copyData || !simpleCanvasRef?.current) {
        return;
      }

      if (!storyboardRef.current) {
        return;
      }

      const boundingBox =
        useExtractElementStore.getState().selectedElementBoundingBox;

      // Create temporary storyboard for dragging
      const tempStoryboard = initializeStoryboardRef.current(
        storyboardRef.current.x +
          event.offsetX -
          elementDragStateRef.current.offsetX,
        storyboardRef.current.y +
          event.offsetY -
          elementDragStateRef.current.offsetY,
        boundingBox?.width || 200,
        boundingBox?.height || 100,
        TEMP_ELEMENT_DRAG_STORYBOARD_ID,
        "Dragged Element",
      );

      // Attach the extra metadata for element drag
      tempStoryboard._elementDragData = copyData;
      tempStoryboard._elementDragOffsetX = elementDragStateRef.current.offsetX;
      tempStoryboard._elementDragOffsetY = elementDragStateRef.current.offsetY;
      if (selectedNavNode.parent?.children?.length) {
        tempStoryboard._elementDragIndex =
          selectedNavNode.parent.children.indexOf(selectedNavNode);
      }

      const htmlShell = useIframePoolStore.getState().latestHtmlShell;
      if (htmlShell) {
        const htmlForInstantUpdate = createInstantUpdateWithShell(
          htmlShell,
          copyData.htmlForInstantUpdate ?? "",
          {
            position: "static",
          },
        );
        useIframePoolStore
          .getState()
          .setHtmlForStoryboard(tempStoryboard.id, htmlForInstantUpdate);
      }

      const isAbsolute = position === "absolute" || position === "fixed";

      if (isAbsolute && elementDragStateRef.current.draggingElement) {
        elementDragStateRef.current.originalDisplay =
          elementDragStateRef.current.draggingElement.style.display;
        elementDragStateRef.current.draggingElement.style.display = "none";
      }

      if (isAbsolute) {
        // Immediately set the parent as the hovered element so we can drop
        dispatch(
          setHoveredElementKey(selectedNavNode.parent?.tempoElement.getKey()),
        );
      }

      useCanvasStore
        .getState()
        .setTemporaryStoryboardForDrag(tempStoryboard, isAbsolute, position);
      elementDragStateRef.current.draggingNavTreeNode = selectedNavNode;
      elementDragStateRef.current.isDragging = true;
      elementDragStateRef.current.isDraggingAbsolute = isAbsolute;
      elementDragStateRef.current.temporaryStoryboardId =
        TEMP_ELEMENT_DRAG_STORYBOARD_ID;

      // Start dragging the temporary storyboard
      simpleCanvasRef.current.startDragItem(tempStoryboard, {
        canvasX: storyboardRef.current.x + event.offsetX,
        canvasY: storyboardRef.current.y + event.offsetY,
      });
    },
    [
      storyboardRef,
      getCopyDataForSelectedElementsRef,
      initializeStoryboardRef,
      simpleCanvasRef,
      dispatch,
    ],
  );

  const onMouseDown = useCallback(
    (
      event: MouseEvent,
      selectedNavNode: NavTreeNode | null,
      domElement: HTMLElement | null,
    ) => {
      if (!selectedNavNode || !domElement) {
        return;
      }

      const boundingRect = domElement.getBoundingClientRect();
      if (!boundingRect) {
        return;
      }

      // Store initial drag information
      elementDragStateRef.current.startX = event.offsetX;
      elementDragStateRef.current.startY = event.offsetY;
      elementDragStateRef.current.offsetX = event.offsetX - boundingRect.left;
      elementDragStateRef.current.offsetY = event.offsetY - boundingRect.top;
      elementDragStateRef.current.originalSelectedElement =
        selectedNavNode.tempoElement;
      elementDragStateRef.current.currentStoryboardId = storyboardIdRef.current;
      elementDragStateRef.current.draggingElement = domElement;
    },
    [storyboardIdRef],
  );

  const onMouseUp = useCallback(() => {
    const dragState = elementDragStateRef.current;
    const domElement = dragState.draggingElement;

    if (elementDragStateRef.current.isDragging && domElement) {
      domElement.style.display =
        elementDragStateRef.current.originalDisplay ?? "";
    }

    // Reset drag state
    elementDragStateRef.current = {
      isDragging: false,
      isDraggingAbsolute: false,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      temporaryStoryboardId: null,
      originalSelectedElement: null,
      currentStoryboardId: null,
      draggingElement: null,
      draggingNavTreeNode: null,
      originalDisplay: null,
    };
  }, []);

  const dragFinishedTrigger = useIframeStore(
    (state) => state.dragFinishedTrigger,
  );
  useEffect(() => {
    onMouseUp();
  }, [dragFinishedTrigger, onMouseUp]);

  const onMouseMove = useCallback(
    async (
      event: {
        buttons: number;
        offsetX: number;
        offsetY: number;
      },
      selectedNavNode: NavTreeNode | null,
      isDrawing: boolean,
    ): Promise<boolean> => {
      const dragState = elementDragStateRef.current;

      // No longer dragging, can just skip
      if (event.buttons === 0 || !dragState.originalSelectedElement) {
        return false;
      }

      // Check if we should start dragging (either absolute or storyboard)
      if (!dragState.isDragging && selectedNavNode && !isDrawing) {
        if (
          !selectedNavNode.parent &&
          storyboardRef.current?.type !== STORYBOARD_TYPES.APPLICATION
        ) {
          return false;
        }

        const deltaX = Math.abs(event.offsetX - dragState.startX);
        const deltaY = Math.abs(event.offsetY - dragState.startY);

        if (deltaX > 5 || deltaY > 5) {
          // Check if element is absolutely positioned
          const position =
            useCanvasStore.getState().selectedElementCssEvals?.["position"];

          await startStoryboardDrag(event, selectedNavNode, position as string);
          return true;
        }
      }

      return false;
    },
    [startStoryboardDrag, storyboardRef],
  );

  return {
    isDragging: elementDragStateRef.current.isDragging,
    isDraggingAbsolute: elementDragStateRef.current.isDraggingAbsolute,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
};
